import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { DeleteConfirmModal } from 'features/support-ticket/components/DeleteConfirmModal';
import { useSupportTickets } from 'features/support-ticket/hooks/useSupportTickets';
import { usePrefetchSupportTickets } from 'features/support-ticket/hooks/usePrefetchSupportTickets';
import { useURLSync } from 'features/support-ticket/hooks/useURLSync';
import { supportTicketServices } from 'features/support-ticket/services/support-ticket.service';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  Search,
  Trash2,
  X,
  AlertCircle,
  Headphones,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from 'utils/dateFormatter';

const SupportTicket = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useURLSync({
    page: '1',
    limit: '10',
    status: 'all',
    category: 'all',
    priority: 'all',
    isEscalated: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [searchInput, setSearchInput] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const { data, isPending, isError, error } = useSupportTickets(filters);
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchSupportTickets();

  useEffect(() => {
    if (data?.pagination) {
      const { currentPage, totalPages } = data.pagination;
      prefetchNextPage(filters, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage(filters);
      }
    }
  }, [data, filters]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortOrder: newOrder,
    }));
  };

  const tickets = data?.data || [];

const handleExport = async () => {
  try {
    const response =
      await supportTicketServices.exportTickets(
        {
          status:
            filters.status,

          category:
            filters.category,

          priority:
            filters.priority,

          isEscalated:
            filters.isEscalated,

          search:
            filters.search,

          sortBy:
            filters.sortBy,

          sortOrder:
            filters.sortOrder,
        }
      );

    const blob = new Blob(
      [response.data],
      {
        type: 'text/csv',
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download = `support-tickets-${Date.now()}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    window.URL.revokeObjectURL(
      url
    );
  } catch (error) {
    console.error(
      'Failed to export tickets',
      error
    );
  }
};

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column)
      return <span className='text-muted-foreground'>↕</span>;
    return filters.sortOrder === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const handleEditClick = (ticket) => {
    navigate(`/support-tickets/${ticket._id}/edit`);
  };

  const handleDeleteClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsDeleteModalOpen(true);
  };

  const handleViewClick = (ticket) => {
    navigate(`/support-tickets/${ticket._id}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      in_progress: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      pending_customer: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      pending_seller: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      resolved: 'bg-green-500/10 text-green-600 dark:text-green-400',
      closed: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
      reopened: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      medium: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      high: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
      urgent: 'bg-red-500/10 text-red-600 dark:text-red-400',
    };
    return (
      colors[priority] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400'
    );
  };

  return (
    <Layout>
      <div className='min-h-screen bg-background text-foreground p-6'>
        <div className={PAGE_CONTENT_WIDTH}>
          {/* Header */}
          <PageHeader
            icon={Headphones}
            title="Support Tickets"
            subtitle="Manage customer support tickets and queries"
            actions={
              !isError && (
                <button
                  onClick={handleExport}
                  className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
              )
            }
          />

          {/* Filters */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 mb-6'>
            {/* Search */}
            <div className='relative lg:col-span-2'>
              <Search
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
                size={16}
              />
              <input
                type='text'
                placeholder='Search'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className='w-full pl-10 pr-10 py-2 text-sm'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Status</option>
              <option value='open'>Open</option>
              <option value='in_progress'>In Progress</option>
              <option value='pending_customer'>Pending Customer</option>
              <option value='pending_seller'>Pending Seller</option>
              <option value='resolved'>Resolved</option>
              <option value='closed'>Closed</option>
              <option value='reopened'>Reopened</option>
            </select>

            {/* Category Filter */}
            <select
              value={filters.category}
              onChange={(e) => updateFilter('category', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Categories</option>
              <option value='booking_issue'>Booking Issue</option>
              <option value='payment_issue'>Payment Issue</option>
              <option value='service_quality'>Service Quality</option>
              <option value='cancellation_refund'>Cancellation/Refund</option>
              <option value='technical_issue'>Technical Issue</option>
              <option value='account_issue'>Account Issue</option>
              <option value='other'>Other</option>
            </select>

            {/* Priority Filter */}
            <select
              value={filters.priority}
              onChange={(e) => updateFilter('priority', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Priorities</option>
              <option value='low'>Low</option>
              <option value='medium'>Medium</option>
              <option value='high'>High</option>
              <option value='urgent'>Urgent</option>
            </select>

            {/* Items per page */}
            <select
              value={filters.limit}
              onChange={(e) => updateFilter('limit', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='5'>5 per page</option>
              <option value='10'>10 per page</option>
              <option value='20'>20 per page</option>
              <option value='50'>50 per page</option>
            </select>
          </div>

          {/* Table */}
          <div className='bg-card border border-border rounded-lg overflow-hidden elevation-1'>
            <div className='overflow-x-auto custom-scrollbar'>
              <table className='w-full'>
                <thead className='bg-muted border-b border-border'>
                  <tr>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase hover:text-foreground transition-hover'
                      // onClick={() => handleSort('ticketNumber')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Ticket ID</span>
                        {/* <SortIcon column='ticketNumber' /> */}
                      </div>
                    </th>
                    {/* <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('subject')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Subject</span>
                        <SortIcon column='subject' />
                      </div>
                    </th> */}
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Name
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Category
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('priority')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Priority</span>
                        <SortIcon column='priority' />
                      </div>
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('status')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Status</span>
                        <SortIcon column='status' />
                      </div>
                    </th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Assigned To
                    </th> */}
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground transition-hover'
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Created</span>
                        <SortIcon column='createdAt' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border'>
                  {isPending ? (
                    <tr>
                      <td
                        colSpan={9}
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        Loading tickets...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan={9}
                        className='px-4 py-8 text-center text-destructive'
                      >
                        Error loading tickets:{' '}
                        {error?.message || 'Something went wrong'}
                      </td>
                    </tr>
                  ) : data?.data?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        No tickets found
                      </td>
                    </tr>
                  ) : (
                    data?.data?.map((ticket) => (
                      <tr
                        key={ticket._id}
                        className='hover:bg-accent/50 transition-colors'
                      >
                        {/* Ticket Number */}
                        <td className='px-4 py-3 text-sm'>
                          <div className='flex items-center space-x-2'>
                            <span>{ticket.ticketNumber}</span>
                            {ticket.isEscalated && (
                              <AlertCircle
                                size={14}
                                className='text-red-600 dark:text-red-400'
                                title='Escalated'
                              />
                            )}
                          </div>
                        </td>

                        {/* Subject */}
                        {/* <td className='px-4 py-3 text-sm font-medium max-w-xs'>
                          <div
                            className='max-w-[100px] truncate'
                            title={ticket.subject}
                          >
                            {ticket.subject}
                          </div>
                        </td> */}

                        {/* Customer */}
                        <td className='px-4 py-3 text-sm'>
  <div>
    <div className='font-medium'>
      {ticket.statusHistory[0].customerId
        ? `${ticket.customerId?.firstName} ${ticket.customerId?.lastName}`
        : `${ticket.sellerId?.firstName} ${ticket.sellerId?.lastName}`}
    </div>

    <div className='text-xs text-muted-foreground'>
      {ticket.customerId ? 'Customer' : 'Service Provider'}
    </div>
  </div>
</td>

                        {/* Category */}
                        <td className='px-4 py-3 text-sm'>
                          <span className='px-2 py-1 text-xs font-medium rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 whitespace-nowrap'>
                            {ticket.category?.replace(/_/g, ' ')}
                          </span>
                        </td>

                        {/* Priority */}
                        <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(
                              ticket.priority,
                            )}`}
                          >
                            {ticket.priority}
                          </span>
                        </td>

                        {/* Status */}
                        <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${getStatusColor(
                              ticket.status,
                            )}`}
                          >
                            {ticket.status?.replace(/_/g, ' ')}
                          </span>
                        </td>

                        {/* Assigned To */}
                        {/* <td className='px-4 py-3 text-sm'>
                          {ticket.assignedTo ? (
                            <div>
                              <div className='font-medium'>
                                {ticket.assignedTo?.name}
                              </div>
                              <div className='text-xs text-muted-foreground'>
                                {ticket.assignedTo?.email}
                              </div>
                            </div>
                          ) : (
                            <span className='text-xs text-muted-foreground italic'>
                              Unassigned
                            </span>
                          )}
                        </td> */}

                        {/* Created Date */}
                        <td className='px-4 py-3 text-sm text-muted-foreground'>
                          {formatDateTime(ticket.createdAt)}
                        </td>

                        {/* Actions */}
                        <td className='px-4 py-3 text-sm text-right'>
                          <div className='flex items-center justify-end space-x-2'>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='View Details'
                              onClick={() => handleViewClick(ticket)}
                            >
                              <Eye
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Edit Ticket'
                              onClick={() => handleEditClick(ticket)}
                            >
                              <Edit
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(ticket)}
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Delete Ticket'
                            >
                              <Trash2 size={16} className='text-muted-foreground hover:text-destructive' />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.pagination && (
              <div className='flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0'>
                <div className='text-sm text-muted-foreground'>
                  Showing{' '}
                  {(data.pagination.currentPage - 1) *
                    data.pagination.itemsPerPage +
                    1}{' '}
                  to{' '}
                  {Math.min(
                    data.pagination.currentPage * data.pagination.itemsPerPage,
                    data.pagination.totalItems,
                  )}{' '}
                  of {data.pagination.totalItems} tickets
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() =>
                      updateFilter(
                        'page',
                        String(data.pagination.currentPage - 1),
                      )
                    }
                    disabled={data.pagination.currentPage === 1}
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className='flex items-center space-x-1'>
                    {[...Array(data.pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = data.pagination.currentPage;

                      if (
                        page === 1 ||
                        page === data.pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter('page', String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'bg-card hover:bg-accent border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className='text-muted-foreground'>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter(
                        'page',
                        String(data.pagination.currentPage + 1),
                      )
                    }
                    disabled={
                      data.pagination.currentPage === data.pagination.totalPages
                    }
                    className='p-2 border border-border rounded-md bg-card hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <DeleteConfirmModal
          isDeleteModalOpen={isDeleteModalOpen}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          ticket={selectedTicket}
        />
      </div>
    </Layout>
  );
};

export default SupportTicket;
